import { designVariables } from "@studio3t/design-variables";
import {
  BadgeMode,
  BadgeType,
  ButtonSmallSubtleIcon,
  GenericBadge,
  GenericCheckBox,
  GenericDropdownWithSearch,
  GenericDropdownWithSearchContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
  SwitchButtonLeft,
  Tooltip,
} from "@studio3t/generic-components";
import { useContext, useEffect, useRef } from "react";
import styled, {
  type DefaultTheme,
  StyleSheetManager,
  useTheme,
} from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";

import type { LogLevel } from "@/types/api";

// The /logs filter sidebar (Figma AP_SystemLogs/filter 13017-11998): the
// card's left rail holding everything that shapes the stream — the
// Auto-scroll switch with its info hint (the design's Persist switch is
// parked until its intended behavior is confirmed with design), the job
// filter dropdown, and the level checkboxes with their badges — plus the
// collapse tab riding the bottom-right edge. Collapsed, only a slim rail
// with the mirrored tab stays.

export const SIDEBAR_WIDTH = 196;
const TAB_WIDTH = 50;
const SPACINGS = designVariables.ui.semantic.spacings;
const SPACINGM = designVariables.ui.semantic.spacingm;

/** Every level with its badge face (Figma badges: trace/debug secondary,
 *  info/warning/error in their status colors; "warn" displays as the
 *  design's "warning"). Order matches the design rows. */
export const LOG_LEVEL_ROWS: ReadonlyArray<{
  level: LogLevel;
  label: string;
  icon: Icon;
  type: BadgeType;
}> = [
  {
    level: "trace",
    label: "trace",
    icon: Icon.Trace,
    type: BadgeType.Secondary,
  },
  {
    level: "debug",
    label: "debug",
    icon: Icon.Debug,
    type: BadgeType.Secondary,
  },
  { level: "info", label: "info", icon: Icon.Info, type: BadgeType.Info },
  {
    level: "warn",
    label: "warning",
    icon: Icon.Warning,
    type: BadgeType.Warning,
  },
  { level: "error", label: "error", icon: Icon.Error, type: BadgeType.Error },
];

/** The badge's own Subtle text color — the design paints each badge's
 *  icon in the SAME color as its label (sampled per badge on Figma
 *  13017-12011), not the brighter standalone status-icon colors. The
 *  Secondary badge has no Subtle color token; its text renders in the
 *  inherited body color. */
function badgeIconColor(theme: DefaultTheme, type: BadgeType): string {
  switch (type) {
    case BadgeType.Info:
      return theme.badgeInfoSubtleColor;
    case BadgeType.Warning:
      return theme.badgeWarningSubtleColor;
    case BadgeType.Error:
      return theme.badgeErrorSubtleColor;
    default:
      return theme.semanticTextBody;
  }
}

export const ALL_LOG_LEVELS: ReadonlyArray<LogLevel> = LOG_LEVEL_ROWS.map(
  (r) => r.level,
);

// No closeOnSelect on the rows: the searchable dropdown closes on select
// anyway, and it forwards the flag to the row's DOM node (a React
// unknown-prop error on every open — see OpenPipelineMenu).
function buildJobItems(
  allJobs: string[],
  selected: string,
  onChange: (v: string) => void,
): ListItemProps[][] {
  return [
    [
      {
        id: "all",
        label: "All jobs",
        type: "text" as const,
        value: "all",
        selected: { value: selected === "all", icon: "dot" as const },
        onClick: () => onChange("all"),
      },
      ...allJobs.map((job) => ({
        id: job,
        label: job,
        type: "text" as const,
        value: job,
        selected: { value: job === selected, icon: "dot" as const },
        onClick: () => onChange(job),
      })),
    ],
  ];
}

// The trigger sits inside the searchable dropdown's own context provider,
// the only place its open flag is readable.
function JobFilterTrigger({ jobFilter }: { jobFilter: string }) {
  const { open } = useContext(GenericDropdownWithSearchContext);
  return (
    <JobTrigger
      type="button"
      $active={open}
      data-testid="logs-job-filter"
      title={jobFilter === "all" ? undefined : jobFilter}
    >
      <JobTriggerLabel>
        {jobFilter === "all" ? "All jobs" : jobFilter}
      </JobTriggerLabel>
      <IconSVG
        icon={Icon.BaseChevron}
        size={10}
        svgColorKey={SVGColorKey.semanticTextBody}
      />
    </JobTrigger>
  );
}

// The collapse tab (Figma 13017-12029): a passive bordered container
// with the standard S subtle icon button inside — the lib button drops
// aria/title props, so they land via the ref (the PipelineNavBar
// useLabeledButton recipe, kept local to avoid a cross-page import).
function CollapseTabButton({
  label,
  expanded,
  onClick,
}: {
  label: string;
  expanded: boolean;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const btn = ref.current;
    if (!btn) return;
    btn.setAttribute("aria-label", label);
    btn.setAttribute("title", label);
    btn.setAttribute("aria-expanded", String(expanded));
    btn.setAttribute("type", "button");
  }, [label, expanded]);
  return (
    <CollapseTab $flipped={!expanded}>
      <ButtonSmallSubtleIcon
        ref={ref}
        icon={Icon.CollapseLeft}
        onClick={onClick}
      />
    </CollapseTab>
  );
}

// The ⓘ hint after a switch label (the design's labelSupportive face —
// the lib switch has no info-icon slot, so it rides alongside).
function InfoHint({ text }: { text: string }) {
  return (
    // "multiline" wraps the hint into a narrow block instead of one
    // viewport-wide line.
    <Tooltip text={text} side="bottom" type="multiline">
      <InfoIconWrap>
        <IconSVG
          icon={Icon.Info}
          size={16}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </InfoIconWrap>
    </Tooltip>
  );
}

export interface LogsFilterSidebarProps {
  autoScroll: boolean;
  setAutoScroll: (v: boolean) => void;
  jobFilter: string;
  onJobChange: (v: string) => void;
  allJobs: string[];
  selectedLevels: ReadonlySet<LogLevel>;
  toggleLevel: (level: LogLevel) => void;
  setAllLevels: (on: boolean) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}

// The level tree, split out to keep the sidebar component readable.
function LevelsSection({
  selectedLevels,
  toggleLevel,
  setAllLevels,
}: Pick<
  LogsFilterSidebarProps,
  "selectedLevels" | "toggleLevel" | "setAllLevels"
>) {
  const theme = useTheme();
  const allSelected = selectedLevels.size === ALL_LOG_LEVELS.length;
  const someSelected = selectedLevels.size > 0;
  return (
    <Section>
      <GenericCheckBox
        label="All levels"
        value={allSelected}
        intermediate={someSelected && !allSelected}
        onChange={() => setAllLevels(!allSelected)}
        testId="logs-all-levels"
      />
      <LevelRows>
        {LOG_LEVEL_ROWS.map((row) => (
          <LevelRow key={row.level}>
            <GenericCheckBox
              value={selectedLevels.has(row.level)}
              onChange={() => toggleLevel(row.level)}
              testId={`logs-level-${row.level}`}
            />
            {/* The icon rides as a child, not the badge's icon prop —
                the prop renders 16px, the design badge carries 12px
                (the badge's own flex gap spaces it). */}
            <GenericBadge type={row.type} mode={BadgeMode.Subtle}>
              <IconSVG
                icon={row.icon}
                size={12}
                colors={{ default: badgeIconColor(theme, row.type) }}
              />
              {row.label}
            </GenericBadge>
          </LevelRow>
        ))}
      </LevelRows>
    </Section>
  );
}

export function LogsFilterSidebar({
  autoScroll,
  setAutoScroll,
  jobFilter,
  onJobChange,
  allJobs,
  selectedLevels,
  toggleLevel,
  setAllLevels,
  collapsed,
  setCollapsed,
}: LogsFilterSidebarProps) {
  if (collapsed) {
    return (
      // The whole rail is the expand target with hover feedback, like the
      // newStudio collapsed strips; keyboard access rides the inner
      // button, and both fire the same idempotent expand, so a click
      // bubbling through the button can't double-toggle.
      <CollapsedRail
        data-testid="logs-filter-collapsed"
        title="Expand filters"
        onClick={() => setCollapsed(false)}
      >
        <CollapsedLabel>Filters</CollapsedLabel>
        <CollapseTabButton
          label="Expand filters"
          expanded={false}
          onClick={() => setCollapsed(false)}
        />
      </CollapsedRail>
    );
  }

  return (
    <Sidebar data-testid="logs-filter-sidebar">
      {/* Every section scrolls as one on a short screen while the card
          keeps its height (TTTL-186); the collapse tab stays put below. */}
      <SidebarScroll>
        {/* The lib switch forwards a non-transient `labelPosition` to its
          label span (React unknown-prop warning) — same filter as
          App.tsx's NewStudioSwitch until the lib renames it. */}
        <StyleSheetManager
          shouldForwardProp={(prop) => prop !== "labelPosition"}
        >
          <Section>
            <SwitchRow>
              <SwitchButtonLeft
                label="Auto-scroll"
                checked={autoScroll}
                onChange={() => setAutoScroll(!autoScroll)}
                testId="logs-autoscroll-switch"
              />
              <InfoHint text="Follow new logs as they arrive; scrolling up pauses it" />
            </SwitchRow>
          </Section>
        </StyleSheetManager>
        <Divider />
        <Section>
          <FieldLabel>Filter by jobs</FieldLabel>
          {/* Searchable: a long job list is filtered by typing (the
              library's built-in search box above the rows). */}
          <GenericDropdownWithSearch
            dropdownItems={buildJobItems(allJobs, jobFilter, onJobChange)}
            inputAsTrigger={false}
            inputPlaceholder="Search jobs"
            noItemsLabel="No jobs found"
            elWidth={{ strategy: "fixed", size: 220 }}
            placement="bottom-start"
          >
            <JobFilterTrigger jobFilter={jobFilter} />
          </GenericDropdownWithSearch>
        </Section>
        <Divider />
        <LevelsSection
          selectedLevels={selectedLevels}
          toggleLevel={toggleLevel}
          setAllLevels={setAllLevels}
        />
      </SidebarScroll>
      <CollapseTabButton
        label="Collapse filters"
        expanded
        onClick={() => setCollapsed(true)}
      />
    </Sidebar>
  );
}

const Sidebar = styled.div`
  position: relative;
  width: ${SIDEBAR_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.cards1Background};
  border-right: 1px solid ${(p) => p.theme.cards1Border};
  /* Carries the card's bottom-left corner — the card no longer
     overflow-clips (the collapse tab overlaps its borders), and the
     header bar above owns the top corners. */
  border-radius: 0 0 0 8px;
`;

/* semantic/spacingl (18px) isn't exported by design-variables yet —
   hardcoded like the other missing tokens. */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACINGS}px;
  padding: 18px ${SPACINGM}px;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  background: ${(p) => p.theme.cards1Border};
`;

/* The sidebar's sections, scrolling as one on the app's overlay
   scrollbars. The bottom padding lets the last rows scroll clear of the
   collapse tab that overlays the sidebar's bottom-right corner. */
const SidebarScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  padding-bottom: 44px;
`;

/* The design switch row is an even 20px (Figma instances h=20); the lib
   switch renders 24 because its label wrapper reserves an empty
   supportive line (20px label + 4px gap + 0-height span) and the root
   top-aligns — which paints the track 2px above the mock. Collapse the
   phantom line and center, so the control is an honest 20px row. */
const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.input.gapinternal}px;
  height: 20px;

  & > div {
    height: 20px;
    align-items: center;
  }

  & > div > div {
    gap: 0;
  }
`;

/* A 20px centering box for the 16px glyph: the Tooltip's own wrapper
   pins content to the row top, so the row's align-items never reaches
   the icon — without the explicit height the ⓘ rides 2px high. */
const InfoIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
`;

/* The "All jobs" dropdown trigger — the design's M secondary button with
   the small 10px chevron (Figma 13017-12006, 85x32). Hand-rolled like
   WorkspaceControls' SecondaryTrigger: the prebuilt lib buttons render
   every icon at 16px, which oversizes the chevron and widens the button
   past the mock. */
const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

const JobTrigger = styled.button<{ $active?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  /* A long selected job name stops at the section padding and
     ellipsizes instead of pushing past it; the chevron stays put. */
  max-width: 100%;

  img,
  svg {
    flex-shrink: 0;
  }
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => p.theme.buttonSecondaryDefaultText};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonSecondaryActiveBackground
      : p.theme.buttonSecondaryDefaultBackground};
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.theme.buttonSecondaryActiveBorder
        : p.theme.buttonSecondaryDefaultBorder};
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
    border-color: ${(p) => p.theme.buttonSecondaryHoverBorder};
  }
`;

const JobTriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Figma Form/label: Inter Medium 14 on input/textlabel, centered in a
   20px-high box. */
const FieldLabel = styled.span`
  display: flex;
  align-items: center;
  height: 20px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${(p) => p.theme.inputLabel};
`;

/* The children hang off a borderlight rule, indented one spacing on each
   side of it (Figma 13017-12010/12011). The inner spacing is 10px minus
   the 1px rule — Figma strokes draw inside the padding, CSS borders add
   to it — landing the rows at the mock's x=34. */
const LevelRows = styled.div`
  margin-left: ${SPACINGS}px;
  padding-left: ${SPACINGS - 1}px;
  border-left: 1px solid ${(p) => p.theme.semanticBorderLight};
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS}px;
`;

const LevelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${designVariables.primitives.spacings.s}px;
`;

/* The collapse tab hugging the sidebar's bottom-right edge (Figma
   13017-12029): a 50px container with rounded left corners and borderdark
   on the three open sides, centring the S subtle icon button. Collapsed,
   the same tab points back out (icon mirrored). */
const CollapseTab = styled.div<{ $flipped?: boolean }>`
  position: absolute;
  /* Sits ON both borders, keeping both visible (Figma render, checked
     pixel by pixel): the sidebar's right divider runs CONTINUOUSLY
     through the tab's height and closes its open right side — so the
     tab stays inside it (right: 0) — while the tab's bottom row IS the
     card's bottom border line: it dips one pixel into the border row,
     and its own bottom border wears the card-border color so the shared
     row reads as one card line, with the rounded corner's arc stroked
     all the way around. */
  right: 0;
  bottom: -1px;
  width: ${TAB_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Figma's spacings/m (6px) minus the 1px border — Figma strokes draw
     inside, CSS borders add outside; this keeps the tab at the mock's
     40px overall height. */
  padding: ${designVariables.primitives.spacings.m - 1}px 0;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.semanticBorderDark};
  border-right: none;
  border-bottom-color: ${(p) => p.theme.cards1Border};
  border-radius: ${designVariables.primitives.corner.xl}px 0 0
    ${designVariables.primitives.corner.xl}px;

  svg,
  img {
    transform: rotate(${(p) => (p.$flipped ? 180 : 0)}deg);
  }
`;

/* Collapsed, the rail keeps the tab's own width and the tab loses its
   borders entirely — the card's own left/bottom borders and the divider
   already outline the corner. The tab drops into the flow at the rail's
   bottom, the label rides right above it (the newStudio strips' 6px
   gap), and the whole rail hovers/clicks as one expand target. */
const CollapsedRail = styled.div`
  position: relative;
  width: ${TAB_WIDTH}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: ${designVariables.primitives.spacings.m}px;
  background: ${(p) => p.theme.cards1Background};
  border-right: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 0 0 0 8px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }

  ${CollapseTab} {
    position: static;
    width: 100%;
    border: none;
    background: transparent;
  }
`;

/* Same treatment as the newStudio collapsed panels' labels (Explorer /
   AI Helper strips) — only anchored at the bottom here. */
const CollapsedLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;
